import { hexToRgb } from '@chiroup/core/functions/hexToRgb';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import healthcomLogo from '../assets/healthcom-logo.png';
import { HEALTHCOM_ACCENT, HEALTHCOM_PRIMARY } from '../constants/brand';
import useLocation from '../hooks/useLocation';
import { useTheme } from '../hooks/useTheme';
import { portalSession } from '../services/portalSession';
import userService from '../services/user.service';
import { Skeleton } from './common/Skeleton';
import { ScheduleForm } from './Container';
import { NotFoundSchedule } from './NotFoundSchedule';
import Schedule from './Schedule';
import ScheduleSkeleton from './ScheduleSkeleton';

// Custom auth replaces Cognito. Local stand-in that maps the stored
// patient context into the same `AuthUser` shape this component used.
type AuthUser = { userId: string; username: string };

const getCurrentUser = async (): Promise<AuthUser> => {
  const patient = portalSession.getPatient();

  if (!portalSession.isLikelyValid() || !patient) {
    throw new Error('Not authenticated');
  }

  return { userId: patient.patientId, username: patient.patientId };
};

const defaultValues: ScheduleForm = {
  treatment: null,
  provider: null,
  date: null,
  startTime: null,
  endTime: null,
  time: null,
  patient: null,
  fname: null,
  lname: null,
  email: null,
  phone: null,
  dateOfBirth: null,
  creditCardConsent: false,
  smsConsent: null,
  smsConsentMethod: null,
};

/**
 * Public booking shell. Reachable at `/clinic-schedule/:clinicPath` —
 * the legacy `schedule.healthcom.io/<path>` URL pattern.
 *
 * Lets a brand-new visitor pick a treatment, provider, and time without
 * being challenged for a Cognito session up front. The auth gate lives
 * inside the booking flow itself: `Schedule` → `ScheduleConfirm` →
 * (when `me` is null) the embedded `<Login>` collects email/phone +
 * DOB, fires the existing `/login` endpoint, OTPs the user in, and
 * then submits the appointment. Existing patients are matched against
 * the clinic's roster by email/phone+DOB; new visitors land as
 * `pending: true` Patient rows via the existing `appointmentCreate`
 * pending-patient code path.
 *
 * Deliberately renders WITHOUT Container's chrome (no sidebar, no
 * BottomNav, no PatientPortalContext provider). Visitors haven't
 * picked a clinic yet — the URL path is the clinic.
 */
const PublicSchedule = () => {
  const { resolved: themeResolved, toggle: toggleTheme } = useTheme();

  // Two route shapes mount this component:
  //   /:clinicPath                      (canonical, e.g. schedule.healthcom.io/product-clinic)
  //   /clinic-schedule/:clinicPath/*    (legacy alias)
  // Both register the param as `clinicPath`, so a single `useParams()`
  // read works for either.
  const { clinicPath = '' } = useParams<{ clinicPath: string }>();

  const { data: clinicLocationData, isFetching } = useLocation(clinicPath);

  const [user, setUser] = useState<AuthUser | null>(null);
  const [me, setMe] = useState<{ patients: any[] } | null>(null);
  const [values, setValues] = useState<ScheduleForm>(defaultValues);

  // If the visitor already has a Cognito session (e.g. they came back
  // to book another appointment), pick it up so ScheduleConfirm can
  // skip the login form.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const current = await getCurrentUser();

        if (!cancelled && current) setUser(current);
      } catch {
        /* not signed in — that's expected for the public flow */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // /me/{path} fetcher used after the embedded Login authenticates the
  // visitor. Mirrors Container's pattern: ref-gated so a single
  // attempt fires per path even if Schedule's effect re-runs.
  const meFetchedForPathRef = useRef<string | null>(null);
  const meFetchInFlightRef = useRef<Promise<any> | null>(null);

  const getMe = useMemo(() => {
    return async () => {
      if (meFetchedForPathRef.current === clinicPath) return undefined;
      if (meFetchInFlightRef.current) return meFetchInFlightRef.current;

      const promise = (async () => {
        try {
          const meRes = await userService.me(clinicPath);
          meFetchedForPathRef.current = clinicPath || null;
          setMe(meRes);

          return meRes;
        } catch {
          // Public flow — failures here are benign. ScheduleConfirm
          // already handles `me=null` by re-rendering its login form.
          meFetchedForPathRef.current = clinicPath || null;

          return undefined;
        } finally {
          meFetchInFlightRef.current = null;
        }
      })();

      meFetchInFlightRef.current = promise;

      return promise;
    };
  }, [clinicPath]);

  // Pre-fetch /me/{path} as soon as we know there's a Cognito session
  // and we have a clinic path to look up against. Without this the
  // booking flow re-prompts already-authed visitors for credentials —
  // ScheduleConfirm renders its login form whenever `me` is null.
  useEffect(() => {
    if (!user || !clinicPath) return;
    void getMe();
  }, [user, clinicPath, getMe]);

  // Apply the clinic's brand colors as soon as we have them. Reuses
  // the same CSS variables the authed portal sets, so the rest of the
  // markup (primary buttons, eyebrow text, etc.) picks them up
  // automatically. Falls back to HealthCom defaults pre-load.
  useEffect(() => {
    const root = document.documentElement;
    const primary = clinicLocationData?.primaryColor || HEALTHCOM_PRIMARY;
    const accent = clinicLocationData?.accentColor || HEALTHCOM_ACCENT;

    try {
      const primaryCsv = hexToRgb(primary);
      root.style.setProperty('--primary-color-base', primaryCsv);
      root.style.setProperty('--primary-color', `rgb(${primaryCsv})`);
    } catch {
      /* bad hex — leave the existing var alone */
    }

    try {
      const accentCsv = hexToRgb(accent);
      root.style.setProperty('--accent-color-base', accentCsv);
      root.style.setProperty('--accent-color', `rgb(${accentCsv})`);
    } catch {
      /* bad hex */
    }
  }, [clinicLocationData?.primaryColor, clinicLocationData?.accentColor]);

  const primaryColor = clinicLocationData?.primaryColor || HEALTHCOM_PRIMARY;

  if (!clinicPath) {
    return <NotFoundSchedule />;
  }

  // Genuine 404 (clinic path doesn't resolve to a location). The
  // `isFetching` branch handled below renders the public shell with
  // skeletons inside, so we only hit NotFound when the fetch has
  // resolved to no data.
  if (!isFetching && !clinicLocationData) {
    return <NotFoundSchedule />;
  }

  const isLoading = isFetching && !clinicLocationData;

  const clinicName =
    (clinicLocationData as any)?.name || (clinicLocationData as any)?.clinicName || 'HealthCom';
  const logoSrc = clinicLocationData?.logo || healthcomLogo;

  return (
    <div className="min-h-dvh flex flex-col antialiased">
      {/* Thin utility strip — sign-in + theme toggle, right-aligned.
          Brand identity is intentionally pushed into the page heading
          below (clinic logo next to clinic name) so this header stays
          unobtrusive — the page IS the booking, not the chrome. */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-[var(--color-chrome-bg)] backdrop-blur-lg border-b border-[var(--color-chrome-border)]">
        <div className="mx-auto max-w-5xl flex items-center justify-end gap-3 px-4 sm:px-6 lg:px-8 py-1.5">
          {user ? (
            <Link
              to="/"
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Go to portal →
            </Link>
          ) : (
            <Link
              to="/"
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Already a patient? Sign in →
            </Link>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={themeResolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex size-7 items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-white/5"
          >
            {themeResolved === 'dark' ? (
              <SunIcon className="size-4" />
            ) : (
              <MoonIcon className="size-4" />
            )}
          </button>
        </div>
      </header>

      <main className="isolate flex-1">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Page heading — eyebrow on top, clinic logo + name inline
              beneath. The brand lives in the page (not the header) so
              the chrome stays a quiet utility strip. */}
          <div className="mb-6">
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: primaryColor }}
            >
              Book a visit
            </p>
            <div className="mt-1 flex items-center gap-3">
              {isLoading ? (
                <>
                  <Skeleton className="h-12 w-12 rounded-md shrink-0" />
                  <Skeleton className="h-7 sm:h-8 w-56" />
                </>
              ) : (
                <>
                  <img className="h-12 w-auto shrink-0" src={logoSrc} alt="" />
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                    {clinicName}
                  </h1>
                </>
              )}
            </div>
          </div>

          {isLoading ? (
            <ScheduleSkeleton />
          ) : (
            <Schedule
              mode="public"
              clinicLocationData={clinicLocationData}
              path={clinicPath}
              user={user}
              setUser={setUser}
              getMe={getMe}
              me={me}
              values={values}
              setValues={setValues}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicSchedule;
